import React, { useState, useEffect } from "react";
import apiClient from "../utils/apiClient";
import DashboardLayout from "../Dashboard/DashboardLayout";
import server from "../utils/serverConfig";
import { Info } from "lucide-react";
import ExitPayloadModal from "./ExitPayloadModal";

// Hardcoded URL as requested
const API_BASE_URL = `${server.ss_dhan.baseUrl}`;

const DeveloperAccess = () => {
    const [openOrders, setOpenOrders] = useState({});
    const [openPositions, setOpenPositions] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [orderFilter, setOrderFilter] = useState("ALL"); // ALL, BUY, SELL

    // --- Exit Modal State ---
    const [exitModalOpen, setExitModalOpen] = useState(false);
    const [positionsToExit, setPositionsToExit] = useState([]);
    const [isExiting, setIsExiting] = useState(false);

    // --- Qty Calculator State ---
    const [calcAsset, setCalcAsset] = useState("NIFTY");
    const [calcTotalQty, setCalcTotalQty] = useState("");
    const [calcPercentage, setCalcPercentage] = useState("50");
    const [calcResult, setCalcResult] = useState(0);

    const LOT_SIZES = {
        'BANKNIFTY': 30,
        'FINNIFTY': 60,
        'NIFTY': 65,
        'SENSEX': 20,
        'MIDCAP': 120
    };

    useEffect(() => {
        const total = parseFloat(calcTotalQty);
        const percent = parseFloat(calcPercentage);
        if (isNaN(total) || isNaN(percent) || total <= 0) {
            setCalcResult(0);
            return;
        }

        const lotSize = LOT_SIZES[calcAsset] || 1;
        // Formula: round((total qty * x% ) / lot_size) * lot_size
        const result = Math.round((total * (percent / 100)) / lotSize) * lotSize;
        setCalcResult(result);
    }, [calcAsset, calcTotalQty, calcPercentage]);

    const fetchOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`${API_BASE_URL}get-all-open-orders-greek`);
            if (response.data.status === 200) {
                setOpenOrders(response.data.data);
            } else {
                setError(response.data.message || "Failed to fetch orders");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchPositions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`${API_BASE_URL}get-all-open-positions-greek`);
            if (response.data.status === 200) {
                setOpenPositions(response.data.data);
            } else {
                setError(response.data.message || "Failed to fetch positions");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelOrder = async (clientId, orderId) => {
        if (!window.confirm(`Are you sure you want to cancel order ${orderId}?`)) return;

        try {
            const payload = {
                orders: [{ client_id: clientId, order_id: orderId }]
            };
            const response = await apiClient.post(`${API_BASE_URL}cancel-orders-greek`, payload);
            alert(JSON.stringify(response.data));
            fetchOrders(); // Refresh
        } catch (err) {
            alert(`Error cancelling order: ${err.message}`);
        }
    };

    const handleCancelAllOrders = async (clientId) => {
        // Collect all orders for this client based on filter
        const clientOrders = openOrders[clientId] || [];
        const ordersToCancel = clientOrders
            .filter(o => {
                if (orderFilter === 'ALL') return true;
                if (orderFilter === 'BUY') return o.side === 1 || o.side === '1';
                if (orderFilter === 'SELL') return o.side === 2 || o.side === '2';
                return true;
            })
            .map(o => ({ client_id: clientId, order_id: o.gorderid || o.oms_order_id }));

        if (ordersToCancel.length === 0) {
            alert("No orders to cancel based on current filter.");
            return;
        }

        if (!window.confirm(`Cancel ${ordersToCancel.length} orders for ${clientId}?`)) return;

        try {
            const payload = { orders: ordersToCancel };
            const response = await apiClient.post(`${API_BASE_URL}cancel-orders-greek`, payload);
            alert(JSON.stringify(response.data));
            fetchOrders();
        } catch (err) {
            alert(`Error cancelling orders: ${err.message}`);
        }
    }

    const normalizePositionForModal = (p, clientId) => ({
        ...p,
        client_id: clientId,
        clientId: clientId,
        strategy: p.strategy || p.label || "NO_STRAT",
        net_qty: p.net_qty ?? p.qty ?? Math.abs(p.traded_qty || 0),
        side: p.side || (Number(p.traded_qty) > 0 ? "BUY" : "SELL"),
    });

    const handleExitPosition = (clientId, position) => {
        setPositionsToExit([normalizePositionForModal(position, clientId)]);
        setExitModalOpen(true);
    };

    const handleExitAllClientPositions = (clientId) => {
        const positions = openPositions[clientId] || [];
        if (positions.length === 0) {
            alert("No open positions for this client.");
            return;
        }
        setPositionsToExit(positions.map(p => normalizePositionForModal(p, clientId)));
        setExitModalOpen(true);
    };

    const handleGlobalExitAllPositions = () => {
        let allPositions = [];
        Object.keys(openPositions).forEach(clientId => {
            const positions = openPositions[clientId] || [];
            // Exclude positions with 'GREEKSOFT' label
            const filteredPositions = positions.filter(p => p.label !== 'GREEKSOFT' && p.strategy !== 'GREEKSOFT');
            const mapped = filteredPositions.map(p => normalizePositionForModal(p, clientId));
            allPositions = [...allPositions, ...mapped];
        });
        console.log("PLOS---", allPositions);
        if (allPositions.length === 0) {
            alert("No open positions found across any client.");
            return;
        }

        setPositionsToExit(allPositions);
        setExitModalOpen(true);
    };

    // --- Modal Handlers ---
    const handleRemoveExitPosition = (posToRemove) => {
        setPositionsToExit(prev => prev.filter(p => p !== posToRemove));
    };

    const handleConfirmExits = async (finalQtys, finalTypes) => {
        const remainingPositions = positionsToExit.filter(p => {
            const strat = p.strategy || p.label || "NO_STRAT";
            const key = `${p.client_id || p.clientId}|${p.symbol}|${strat}`;
            const finalQty = finalQtys[key];
            return finalQty && Number(finalQty) > 0;
        });

        if (remainingPositions.length === 0) {
            alert("No positions to exit (all quantities set to 0).");
            setExitModalOpen(false);
            return;
        }

        setIsExiting(true);
        try {
            // Group the remaining positions by client_id since the endpoint expects { client_id: ..., positions: [...] }
            const payloadByClient = {};

            remainingPositions.forEach(p => {
                const strat = p.strategy || "NO_STRAT";
                const key = `${p.client_id || p.clientId}|${p.symbol}|${strat}`;

                const copy = { ...p };
                // Override raw quantities with final requested amounts
                copy.qty = finalQtys[key];
                if (copy.net_qty !== undefined) copy.net_qty = copy.qty;
                if (copy.traded_qty !== undefined) {
                    copy.traded_qty = finalQtys[key] * (p.traded_qty < 0 ? -1 : 1);
                }
                // We keep type as is default for now or inject finalType if APi supports it
                // API exit-positions-greek likely infers SIDE properly from position sign

                const cId = p.client_id || p.clientId;
                if (!payloadByClient[cId]) {
                    payloadByClient[cId] = [];
                }
                payloadByClient[cId].push(copy);
            });

            // Make parallel calls for each client
            const promises = Object.keys(payloadByClient).map(clientId => {
                const payload = {
                    client_id: clientId,
                    positions: payloadByClient[clientId]
                };
                return apiClient.post(`${API_BASE_URL}exit-positions-greek`, payload);
            });

            const results = await Promise.allSettled(promises);

            // Collect statuses
            const failed = [];
            results.forEach((res, index) => {
                if (res.status === 'rejected') {
                    failed.push(`Client Request Failed: ${res.reason.message}`);
                } else if (res.value.data.status !== 200) {
                    failed.push(res.value.data.message || "Unknown error");
                }
            });

            if (failed.length > 0) {
                alert("Some exits failed:\n" + failed.join("\n"));
            } else {
                alert("Successfully dispatched all exit orders.");
            }

            setExitModalOpen(false);
            fetchPositions(); // Refresh after 1.5s usually better, but immediate works too
            setTimeout(fetchPositions, 2000);

        } catch (err) {
            alert(`Error confirming exits: ${err.message}`);
        } finally {
            setIsExiting(false);
        }
    };

    // Helper to render Order Table
    const renderOrderTable = () => {
        return Object.keys(openOrders).map(clientId => {
            const orders = openOrders[clientId];
            if (!orders || orders.length === 0) return null;

            const filteredOrders = orders.filter(o => {
                if (orderFilter === 'ALL') return true;
                // Assuming side 1 = BUY, 2 = SELL. Adjust if API returns different values.
                if (orderFilter === 'BUY') return o.side == 1;
                if (orderFilter === 'SELL') return o.side == 2;
                return true;
            });

            if (filteredOrders.length === 0) return null;

            return (
                <div key={clientId} className="mb-6 bg-gray-800 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-xl font-bold text-white">{clientId} - Orders ({filteredOrders.length})</h3>
                        <button
                            onClick={() => handleCancelAllOrders(clientId)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                        >
                            Cancel All ({orderFilter})
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm text-gray-300">
                            <thead className="bg-gray-700 text-xs uppercase">
                                <tr>
                                    <th className="px-3 py-2">Order ID</th>
                                    <th className="px-3 py-2">Time</th>
                                    <th className="px-3 py-2">Symbol</th>
                                    <th className="px-3 py-2">Side</th>
                                    <th className="px-3 py-2">Qty</th>
                                    <th className="px-3 py-2">Price</th>
                                    <th className="px-3 py-2">Status</th>
                                    <th className="px-3 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {filteredOrders.map((order, idx) => (
                                    <tr key={idx} className="hover:bg-gray-700">
                                        <td className="px-3 py-2 font-mono">{order.gorderid || order.oms_order_id}</td>
                                        <td className="px-3 py-2">{order.order_timestamp || order.exchange_order_time}</td>
                                        <td className="px-3 py-2 font-medium text-white">{order.trading_symbol || order.symbol}</td>
                                        <td className={`px-3 py-2 font-bold ${order.side == 1 ? 'text-green-400' : 'text-red-400'}`}>
                                            {order.side == 1 ? 'BUY' : 'SELL'}
                                        </td>
                                        <td className="px-3 py-2">{order.qty} / {order.org_qty}</td>
                                        <td className="px-3 py-2">{order.price}</td>
                                        <td className="px-3 py-2">{order.order_status}</td>
                                        <td className="px-3 py-2">
                                            <button
                                                onClick={() => handleCancelOrder(clientId, order.gorderid || order.oms_order_id)}
                                                className="text-red-400 hover:text-red-200 underline"
                                            >
                                                Cancel
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        });
    };

    // Helper to render Position Table
    const renderPositionTable = () => {
        return Object.keys(openPositions).map(clientId => {
            const positions = openPositions[clientId];
            if (!positions || positions.length === 0) return null;

            return (
                <div key={clientId} className="mb-6 bg-gray-800 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-xl font-bold text-white">{clientId} - Positions ({positions.length})</h3>
                        <button
                            onClick={() => handleExitAllClientPositions(clientId)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                        >
                            Exit All Positions
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm text-gray-300">
                            <thead className="bg-gray-700 text-xs uppercase">
                                <tr>
                                    <th className="px-3 py-2">Symbol</th>
                                    <th className="px-3 py-2">Net Qty</th>
                                    <th className="px-3 py-2">LTP</th>
                                    <th className="px-3 py-2">P&L</th>
                                    <th className="px-3 py-2">Strategy</th>
                                    <th className="px-3 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {positions.map((pos, idx) => (
                                    <tr key={idx} className="hover:bg-gray-700">
                                        <td className="px-3 py-2 font-medium text-white">{pos.symbol || pos.trading_symbol}</td>
                                        <td className={`px-3 py-2 font-bold ${Number(pos.traded_qty) > 0 ? 'text-green-400' : Number(pos.traded_qty) < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                                            {pos.traded_qty}
                                        </td>
                                        <td className="px-3 py-2">{pos.ltp || '-'}</td>
                                        <td className={`px-3 py-2 ${Number(pos.pnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {pos.pnl ? Number(pos.pnl).toFixed(2) : '-'}
                                        </td>
                                        <td className="px-3 py-2">{pos.label || '-'}</td>
                                        <td className="px-3 py-2">
                                            <button
                                                onClick={() => handleExitPosition(clientId, pos)}
                                                className="bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded text-xs"
                                            >
                                                Exit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        });
    };

    return (
        <DashboardLayout>
            <div className="p-6 bg-gray-900 min-h-screen text-gray-100 w-full overflow-x-hidden">
                <h1 className="text-3xl font-bold mb-6 text-white border-b border-gray-700 pb-2">Developer Access - Greek API</h1>

                {/* Qty Calculation Tool */}
                <div className="mb-8 p-6 bg-blue-900/10 border border-blue-500/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-4">
                        <h2 className="text-xl font-bold text-blue-300">Qty Calculation Tool</h2>
                        <div className="group relative">
                            <Info className="w-4 h-4 text-blue-400 cursor-help" />
                            <div className="absolute left-6 top-0 hidden group-hover:block bg-gray-800 text-xs text-gray-200 p-2 rounded border border-gray-700 w-64 z-50">
                                <strong>Formula:</strong><br />
                                round((Total Qty × Percentage / 100) / Lot Size) × Lot Size
                                <div className="mt-2 text-[10px] text-gray-400">
                                    Lot Sizes: NIFTY(65), BANKNIFTY(30), FINNIFTY(60), SENSEX(20), MIDCAP(120)
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-400 font-medium">Select Asset</label>
                            <select
                                value={calcAsset}
                                onChange={(e) => setCalcAsset(e.target.value)}
                                className="bg-gray-800 text-white border-gray-700 rounded p-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            >
                                {Object.keys(LOT_SIZES).map(asset => (
                                    <option key={asset} value={asset}>{asset}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-400 font-medium">Total Quantity</label>
                            <input
                                type="number"
                                value={calcTotalQty}
                                onChange={(e) => setCalcTotalQty(e.target.value)}
                                placeholder="Enter total qty"
                                className="bg-gray-800 text-white border-gray-700 rounded p-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-400 font-medium">Exit Percentage (%)</label>
                            <input
                                type="number"
                                value={calcPercentage}
                                onChange={(e) => setCalcPercentage(e.target.value)}
                                className="bg-gray-800 text-white border-gray-700 rounded p-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                        </div>

                        <div className="bg-gray-800/50 border border-blue-500/20 p-2 rounded-lg flex flex-col justify-center items-center h-[38px]">
                            <span className="text-xs text-blue-400 font-medium uppercase tracking-wider">Calculated Qty</span>
                            <span className="text-xl font-bold text-white font-mono">{calcResult.toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex gap-4 mb-6 flex-wrap">
                    <button
                        onClick={fetchOrders}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded font-semibold transition"
                    >
                        Get All Open Orders
                    </button>

                    <button
                        onClick={fetchPositions}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded font-semibold transition"
                    >
                        Get All Open Positions
                    </button>

                    <div className="flex items-center space-x-2 bg-gray-800 px-3 rounded">
                        <span className="text-sm font-medium">Filter Orders:</span>
                        <select
                            value={orderFilter}
                            onChange={(e) => setOrderFilter(e.target.value)}
                            className="bg-gray-700 text-white border-none rounded p-1 text-sm focus:ring-0 cursor-pointer"
                        >
                            <option value="ALL">All Sides</option>
                            <option value="BUY">Buy Only</option>
                            <option value="SELL">Sell Only</option>
                        </select>
                    </div>

                    <button
                        onClick={handleGlobalExitAllPositions}
                        disabled={Object.keys(openPositions).length === 0}
                        className={`ml-auto px-4 py-2 font-bold rounded shadow-lg transition flex items-center gap-2 ${Object.keys(openPositions).length === 0
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-red-600 hover:bg-red-500 text-white shadow-red-900/30'
                            }`}
                        title="Aggregates and exits ALL open positions across all clients using the Exit Modal"
                    >
                        Exit All Positions (Global)
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="p-4 mb-4 text-sm text-red-200 bg-red-900 rounded border border-red-700">
                        Error: {error}
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="p-4 text-center text-blue-300 animate-pulse">
                        Fetching data...
                    </div>
                )}

                {/* Data Sections */}
                <div className="grid grid-cols-1 gap-8">

                    {/* Orders Section */}
                    <section>
                        <h2 className="text-2xl font-semibold mb-4 text-blue-300">Open Orders</h2>
                        {Object.keys(openOrders).length === 0 ? (
                            <p className="text-gray-500 italic">No orders fetched or no open orders found.</p>
                        ) : (
                            renderOrderTable()
                        )}
                    </section>

                    {/* Positions Section */}
                    <section className="border-t border-gray-700 pt-8">
                        <h2 className="text-2xl font-semibold mb-4 text-indigo-300">Open Positions</h2>
                        {Object.keys(openPositions).length === 0 ? (
                            <p className="text-gray-500 italic">No positions fetched or no open positions found.</p>
                        ) : (
                            renderPositionTable()
                        )}
                    </section>

                </div>
            </div>

            {/* Exit Payload Modal */}
            <ExitPayloadModal
                isOpen={exitModalOpen}
                onClose={() => setExitModalOpen(false)}
                positions={positionsToExit}
                onConfirm={handleConfirmExits}
                onRemove={handleRemoveExitPosition}
                isLoading={isExiting}
            />
        </DashboardLayout>
    );
};

export default DeveloperAccess;
